/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/DataInput/components/BulkImportModal.tsx.
 */

import React, { useMemo, useRef, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import Button from "../../../../../components/common/Button";
import OutlineButton from "../../../../../components/common/OutlineButton";
import "../../../../../components/common/ConfirmModal.css";
import "./BulkImportModal.css";
import DataCollectionGuideLink from "./DataCollectionGuideLink";
import DataCollectionTemplateLink from "./DataCollectionTemplateLink";

/** Type definition — Bulk Rejected. */
export type BulkRejected = {
  code?: string;
  submission_code?: string;
  recordKey?: string;
  stage?: string;
  row?: number;
  field?: string;
  message: string;
};

/** DB-shaped submission object (same shape as getSubmissionById) for the detail view. */
export type BulkRecordDetail = Record<string, any>;

/** Type definition — Bulk Preview. */
export type BulkPreview = {
  importId: string;
  totalSubmissions?: number;
  acceptedCount?: number;
  rejectedCount?: number;
  accepted?: Array<
    | string
    | {
        code?: string;
        submission_code?: string;
        recordKey?: string;
        material_code?: string;
        materialCode?: string;
        material_name?: string;
        materialName?: string;
        sub_material_code?: string;
        subMaterialCode?: string;
        sub_material_name?: string;
        subMaterialName?: string;
      }
  >;
  rejected?: BulkRejected[];
  details?: Record<string, BulkRecordDetail>;
};

function apiPath(pathWithApiPrefix: string, pathWithoutApiPrefix: string) {
  const baseURL = String((axiosClient.defaults as any)?.baseURL || "");
  const normalized = baseURL.replace(/\/+$/, "");
  const endsWithApi = normalized.endsWith("/api");
  return endsWithApi ? pathWithoutApiPrefix : pathWithApiPrefix;
}

function normalizeBackendMessage(message: string): string {
  return String(message || "")
    .replace(/\bSubmission code\b/gi, "Record key")
    .replace(/\bsubmission code\b/gi, "record key");
}

function summarizeRejections(rejected: BulkRejected[]) {
  const keyOf = (r: BulkRejected) =>
    `${r.stage ?? ""}||${r.field ?? ""}||${String(r.message ?? "").trim()}`;

  const counts = new Map<string, number>();
  for (const r of rejected) {
    const k = keyOf(r);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }

  const top = Array.from(counts.entries())
    .map(([k, count]) => {
      const [stage, field, message] = k.split("||");
      return { count, stage: stage || "-", field: field || "-", message: message || "-" };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 12);

  return { totalRejected: rejected.length, top };
}

function logRejectionSummaryAlways(preview: BulkPreview) {
  const rejected = preview.rejected || [];
  const accepted = preview.accepted || [];

  // Always log summary when there are rejections (user explicitly asked for console reason).
  if (rejected.length === 0) return;

  // eslint-disable-next-line no-console
  console.warn("[bulk-import] preview:rejected", {
    importId: preview.importId,
    totalSubmissions: preview.totalSubmissions,
    acceptedCount: accepted.length,
    rejectedCount: rejected.length,
  });

  const summary = summarizeRejections(rejected);
  // eslint-disable-next-line no-console
  console.groupCollapsed?.("[bulk-import] preview:rejections (top reasons)");
  // eslint-disable-next-line no-console
  console.table?.(summary.top);
  // eslint-disable-next-line no-console
  console.log("[bulk-import] preview:rejections sample", rejected.slice(0, 10));
  // eslint-disable-next-line no-console
  console.groupEnd?.();
}

/** React component — Bulk Import Modal. */
export default function BulkImportModal({
  open,
  onClose,
  onParsed,
}: {
  open: boolean;
  onClose: () => void;
  onParsed: (preview: BulkPreview) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canParse = useMemo(() => !!file && !busy, [file, busy]);

  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFile(null);
  };

  const handleClose = () => {
    if (busy) return;
    setError(null);
    clearFile();
    onClose();
  };

  const handleParse = async () => {
    if (!file) return;
    setError(null);

    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("file", file);

      const previewUrl = apiPath(
        "/api/import/submissions/preview",
        "/import/submissions/preview"
      );

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[bulk-import] preview:start", {
          fileName: file?.name,
          size: file?.size,
          type: file?.type,
          previewUrl,
        });
      }

      const res = await axiosClient.post(previewUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[bulk-import] preview:response", res?.data);
      }

      if (!res?.data?.success || !res?.data?.importId) {
        throw new Error(res?.data?.message || "Preview failed.");
      }

      const preview: BulkPreview = {
        importId: String(res.data.importId),
        totalSubmissions: res.data.totalSubmissions,
        acceptedCount: res.data.acceptedCount,
        rejectedCount: res.data.rejectedCount,
        accepted: res.data.accepted || [],
        rejected: (res.data.rejected || []).map((r: BulkRejected) => ({
          ...r,
          message: normalizeBackendMessage(r?.message),
        })),
        details: res.data.details || {},
      };

      logRejectionSummaryAlways(preview);

      // Close modal then redirect to preview screen via parent.
      onParsed(preview);
      clearFile();
      onClose();
    } catch (e: any) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[bulk-import] preview:error", {
          message: e?.message,
          status: e?.response?.status,
          data: e?.response?.data,
        });
      }
      setError(
        normalizeBackendMessage(
          e?.response?.data?.message || e?.message || "Preview failed."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay bulk-import-modal" role="dialog" aria-modal="true">
      <div className="modal-card fade-in bulk-import-modal__card">
        <div className="modal-header">
          <h2>Import from Excel</h2>
          <button className="modal-close" onClick={handleClose} disabled={busy}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="bulk-import-modal__intro">
            <div className="bulk-import-modal__subtitle">
              Upload the ICoMEC data collection Excel template to preview accepted and
              rejected records.
            </div>
            <div className="bulk-import-modal__resource-links">
              <DataCollectionGuideLink
                className="bulk-import-modal__sample-link"
                label="Data collection guide (PDF)"
              />
              <DataCollectionTemplateLink className="bulk-import-modal__sample-link" />
            </div>
          </div>

          <div className="bulk-import-modal__field">
            <div className="bulk-import-modal__label">Excel file</div>
            <div className="bulk-import-modal__help">
              Accepted format: <strong>.xlsx</strong>
            </div>
          </div>

          <div className="bulk-import-modal__file-row">
            <input
              ref={fileInputRef}
              className="bulk-import-modal__file-input"
              type="file"
              accept=".xlsx"
              onChange={(e) => {
                setError(null);
                setFile(e.target.files?.[0] || null);
              }}
              disabled={busy}
            />

            <div className="bulk-import-modal__file-name" aria-live="polite">
              <span className="bulk-import-modal__file-name-text">
                {file ? file.name : "No file selected (.xlsx)"}
              </span>
              {file && (
                <button
                  type="button"
                  className="bulk-import-modal__clear-x"
                  onClick={() => setFile(null)}
                  disabled={busy}
                  aria-label="Clear selected file"
                >
                  <span className="bulk-import-modal__clear-x-icon">×</span>
                  <span className="bulk-import-modal__clear-x-tooltip" role="tooltip">
                    Clear
                  </span>
                </button>
              )}
            </div>

            <Button
              label="Browse file"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              type="button"
              variant="default"
            />
          </div>

          {busy && (
            <div className="bulk-import-modal__loading" aria-live="polite">
              <span className="bulk-import-modal__spinner" aria-hidden="true" />
              Parsing & validating…
            </div>
          )}

          {error && <div className="bulk-import-modal__error">{error}</div>}
        </div>

        <div className="modal-footer">
          <OutlineButton
            label="Cancel"
            onClick={handleClose}
            disabled={busy}
            type="button"
          />
          <Button
            label={busy ? "Processing..." : "Preview"}
            onClick={handleParse}
            disabled={!canParse}
            type="button"
            variant="primary"
          />
        </div>
      </div>
    </div>
  );
}

